/**
 * The modal shell every confirmation in this app uses.
 *
 * Three things it does that a `<div>` with a grey background does not: Escape closes it, focus
 * moves into it on open and back to whatever opened it on close, and Tab stays inside it. A
 * confirmation dialog that can be tabbed out of behind the overlay is a dialog somebody confirms
 * by accident.
 *
 * `onClose` is deliberately not wired to the backdrop for destructive dialogs — see `dismissible`.
 */

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  title: string;
  children: ReactNode;
  footer: ReactNode;
  onClose: () => void;
  /**
   * Whether Escape and a backdrop click close it. `false` for the one-time token reveal, where
   * closing by accident destroys the only copy of a credential that exists.
   */
  dismissible?: boolean;
  eyebrow?: string;
}

export function Dialog({ title, children, footer, onClose, dismissible = true, eyebrow }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();

  const close = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useEffect(() => {
    opener.current = document.activeElement;
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (focusable.length === 0) return;
      const firstEl = focusable.at(0);
      const lastEl = focusable.at(-1);
      if (firstEl === undefined || lastEl === undefined) return;
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      // Back where it came from, so closing a dialog does not dump focus at the top of the page.
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, [close]);

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panel}>
        {eyebrow !== undefined && <p className="dialog__eyebrow">{eyebrow}</p>}
        <h2 className="dialog__title" id={titleId}>
          {title}
        </h2>
        {children}
        <div className="dialog__footer">{footer}</div>
      </div>
    </div>
  );
}
